<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { IDECONSTANT } from '@/utils/ide'
import { IDEVIEWTABS, TAB_BUTTON_POSITION, TAB_ICONS_NAMES } from '@/utils/tabs'
import { useAdvanceCompilerDatabaseStore } from '@/stores/advanceCompilerDatabase.store'
import advanceCompilerDatabaseService from '@/services/ide/advanceCompilerDatabase.service'
import { useIdeStore } from '@/stores/ide.store'
import editorService from '@/services/ide/editor.service'
import TabCompTitleBar from '@/components/ide/ide/TabCompTitleBar.vue'
import dbTerminalIcon from '@/assets/images/ide/terminaldb.svg'
import animatingLogo from '@/assets/images/ide/animating_logo.gif'
import { INIT_NEXT_ACTION } from '@/components/organisation/enum/advanceCompiler.enums'

const props = defineProps({
  tabPosition: {
    type: String,
    required: false,
    default: TAB_BUTTON_POSITION.BOTTOM
  }
})

const ideStore = useIdeStore()

const advanceCompilerDatabaseStore = useAdvanceCompilerDatabaseStore()

const serverMessageRef = ref<HTMLDivElement | null>(null)

const inprogressTest = computed(() => {
  if (advanceCompilerDatabaseStore.initNextAction == INIT_NEXT_ACTION.WAIT) {
    return 'Something went wrong please wait while we restart the machine...'
  }
  if (advanceCompilerDatabaseStore.isCodeExecutingDb) {
    return 'Stopping Service. Please Wait...'
  } else return 'Starting Service. Please Wait...'
})

const socketInProgress = computed(() => {
  return advanceCompilerDatabaseStore.socketInProgressDb
})

const socketLogsInProgress = computed(() => {
  return advanceCompilerDatabaseStore.socketLogsInProgress
})

const startLoggingMessages = computed(() => {
  return advanceCompilerDatabaseStore.startLoggingMessages
})

const hideCodeEditor = computed(() => {
  return advanceCompilerDatabaseStore.hideOutputEditor
})

const logMessages = computed(() => {
  return advanceCompilerDatabaseStore.databaseLogMessages
})

const executeQueryMessages = computed(() => {
  return advanceCompilerDatabaseStore.logExecuteQueryResult
})

const serverErrorsMessages = computed(() => {
  return advanceCompilerDatabaseStore.serverErrorsMessages
})

const initPodCompleted = computed(() => {
  return advanceCompilerDatabaseStore.isPodInitializationCompleted
})

/**
 * Polls for the IDE editor (Ace editor) or advanced compiler database editor to be ready.
 * This function checks whether `window['ace']` or `useIdeStore().advCompilerDatabaseEditor` is initialized,
 * and if not, retries up to 10 times with a 1-second delay between each attempt.
 * If the editor isn't ready after 10 attempts, it resets the open project ID to `null`.
 * @param count - The current number of polling attempts. Defaults to 0.
 * @returns - Returns `null` if the editor isn't initialized after 10 attempts, otherwise undefined.
 */
const pollForEditorReady = async (count: number = 0) => {
  if (!window['ace'] && !useIdeStore().advCompilerDatabaseEditor) {
    if (count > 10) {
      return null
    } else {
      await new Promise((resolve) => setTimeout(resolve, 1000))
      await pollForEditorReady(count + 1)
    }
  }
}

onMounted(async () => {
  advanceCompilerDatabaseService.initEditorsAdvDatabase()
  if (advanceCompilerDatabaseStore.executeQueryResponse) {
    await pollForEditorReady()
    editorService.setEditorSession(
      IDECONSTANT.ADVCOMPILERTERMINAL,
      advanceCompilerDatabaseStore.executeQueryResponse
    )
  }
})

onUnmounted(() => {
  advanceCompilerDatabaseService.destroyAdvDatabaseEditor()
})

watch(
  () => logMessages.value,
  async () => {
    await nextTick()
    const container = serverMessageRef.value
    if (container) {
      container.scrollTop = container.scrollHeight
    }
  }
)
</script>

<template>
  <div class="surface-primary-base relative flex h-full w-full flex-col">
    <TabCompTitleBar
      v-if="ideStore.currentMobileTab == null && props.tabPosition == TAB_BUTTON_POSITION.MAXIMIZE"
      titleName="Console"
      :icon="dbTerminalIcon"
      :tabPosition="props.tabPosition"
      :tabName="TAB_ICONS_NAMES.DB_TERMINAL"
    />
    <div
      class="background-primary relative h-full w-full min-w-[100%] max-w-[0px]"
      :id="`${IDEVIEWTABS.DB_TERMINAL}-main`"
    >
      <div
        class="background-primary h-full overflow-y-auto border-gray-300 bg-gray-100 text-xs"
        ref="serverMessageRef"
        :class="initPodCompleted ? 'dark:bg-inherit' : 'background-primary px-2'"
      >
        <div
          v-if="serverErrorsMessages != null"
          class="mb-1.5 p-2.5 font-mono text-black dark:text-white"
        >
          <div>{{ serverErrorsMessages }}</div>
        </div>
        <div
          class="mb-1.5 flex h-full flex-col items-center justify-center text-center text-black"
          v-else-if="socketInProgress && !socketLogsInProgress"
        >
          <img :src="animatingLogo" class="mx-auto h-24 w-24 text-black" />
          {{ inprogressTest }}
        </div>
        <div
          v-else-if="startLoggingMessages && logMessages != null"
          class="mb-1.5 font-mono text-black"
        >
          <img :src="animatingLogo" class="h-24 w-24" />
          <div v-for="(message, index) in logMessages" :key="index" class="text-black">
            {{ message }}
          </div>
        </div>

        <div
          class="background-primary h-full w-full min-w-fit dark:bg-inherit"
          v-else-if="hideCodeEditor && executeQueryMessages !== null"
        >
          <div class="background-primary h-full w-full min-w-fit flex-col overflow-x-auto p-2.5">
            <div class="stroke-secondary-default rounded-tl-[3px] rounded-tr-[3px] border-b">
              <div class="stroke-secondary-default flex h-full w-full items-start justify-between">
                <div
                  v-for="(_, key) in executeQueryMessages.length > 0
                    ? executeQueryMessages[0]
                    : executeQueryMessages"
                  :key="key"
                  class="stroke-secodary-default dark:surface-primary-base text-high dark:stroke-primary-default flex min-h-9 min-w-[150px] flex-1 items-center justify-center border-l px-2 text-sm font-normal leading-snug text-black first:border-none dark:text-white"
                >
                  {{ key }}
                </div>
              </div>
            </div>
            <div class="flex items-center justify-between py-4">
              <div
                v-for="(item, index) in executeQueryMessages"
                :key="index"
                class="stroke-secondary-default flex min-h-9 w-full flex-col items-center justify-between gap-4"
              >
                <div
                  v-for="(value, index2) in item"
                  :key="index2"
                  class="stroke-secondary-default flex min-h-11 w-full min-w-[150px] flex-row justify-center p-3 text-sm font-normal leading-snug text-black dark:text-white"
                  :class="[
                    index2 == 0 ? 'mt-2' : 'mt-0',
                    index2 % 2 === 1
                      ? 'surface-secondary-base dark:surface-primary-base'
                      : 'stroke-primary-default'
                  ]"
                >
                  {{ value }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          v-show="!hideCodeEditor && !serverErrorsMessages"
          :id="IDECONSTANT.ADVCOMPILERTERMINAL"
          :class="['editor h-full w-full  bg-gray-100 text-black dark:bg-inherit dark:text-white']"
        ></div>
      </div>
    </div>
  </div>
</template>
